import {gql} from '@apollo/client';

import {
  AnalysisTemplateGraphQL,
  AnalysisTemplateConfigVariableGraphQL,
} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {ObjectType} from 'app-core/framework/CoreObjectConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export const GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY = gql`
  query GetAnalysisTemplateForAssayRun($analysisTemplateId: StrictString!)
  @skipAudit
  @engComponent(name: PIPELINES) {
    analysisTemplate(id: $analysisTemplateId) {
      ... on AnalysisTemplateNode {
        __typename
        id
        name
        configVariables {
          ... on AnalysisTemplateVariableNode {
            __typename
            id
            name
            variableTypeConfigSpec
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_ASSAY_RUN_ANALYSIS_IDS = gql`
  query GetAssayRunAnalysisIds($runId: StrictString!) @skipAudit @engComponent(name: PIPELINES) {
    run(id: $runId) {
      ... on RunNode {
        __typename
        id
        analysisIds
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_ANALYSES_FOR_ASSAY_RUN_QUERY = gql`
  query GetAnalysesForAssayRun($analysisIds: [StrictString!]!) @skipAudit @engComponent(name: PIPELINES) {
    analyses(ids: $analysisIds) {
      ... on AnalysisNode {
        __typename
        id
        name
        createdAt
        statusCode
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// Keep in sync with benchling/results/analysis_template_integration_utils.py
export function convertApiIdToSchemaFieldSystemName(apiIdentifier: string): string {
  const underscoreIndex = apiIdentifier.indexOf('_');
  const prefix = apiIdentifier.substring(0, underscoreIndex);
  let suffix = '_';

  for (let i = underscoreIndex + 1; i < apiIdentifier.length; i++) {
    const char = apiIdentifier[i];
    if (char === char.toUpperCase() && char !== char.toLowerCase()) {
      suffix += `${char.toLowerCase()}_`;
    } else {
      suffix += char;
    }
  }

  return prefix + suffix;
}

interface AnalysisTemplateVariableSchemaFieldSpec {
  name: string;
  systemName: string;
  fieldType: FieldType;
  isMulti: boolean;
  isRequired: boolean;
  variableId: string; // The ID of the analysis config variable
}

/**
 * Set of variable kinds that can be mapped to schema fields
 */
const SCHEMA_FIELD_MAPPED_TEMPLATE_VARIABLE_KINDS = new Set([
  'string',
  'number',
  'integer',
  'boolean',
  'domain',
  'array',
]);

/**
 * Maps domain types to corresponding field types
 */
const DOMAIN_TYPE_TO_FIELD_TYPE_MAP: Partial<Record<ObjectType, FieldType>> = {
  AA_SEQUENCE: FieldType.BIOENTITY_LINK,
  CUSTOM_ENTITY: FieldType.BIOENTITY_LINK,
  DNA_SEQUENCE: FieldType.BIOENTITY_LINK,
  MIXTURE: FieldType.BIOENTITY_LINK,
  MOLECULE: FieldType.BIOENTITY_LINK,
  PLATE: FieldType.STORABLE_LINK,
  BOX: FieldType.STORABLE_LINK,
  CONTAINER: FieldType.STORABLE_LINK,
};

/**
 * Converts an analysis config variable specification to a field type and isMulti flag
 */
export function getFieldTypeAndMultiFromVariableSpec(variableTypeConfigSpec: PipelineConfigJsonSpec): {
  fieldType: FieldType;
  isMulti: boolean;
} {
  // Get the scalar config spec - for arrays, use elementSpec; otherwise use the spec itself
  const scalarConfigSpec =
    variableTypeConfigSpec.kind === 'array' ? variableTypeConfigSpec.elementSpec : variableTypeConfigSpec;

  let isMulti = false;

  let fieldType: FieldType;

  switch (scalarConfigSpec.kind) {
    case 'boolean':
      fieldType = FieldType.BOOLEAN;
      break;
    case 'integer':
      fieldType = FieldType.INTEGER;
      break;
    case 'number':
      fieldType = FieldType.FLOAT;
      break;
    case 'string':
      fieldType = FieldType.TEXT;
      break;
    case 'domain': {
      const domainType = scalarConfigSpec.types[0];
      fieldType = DOMAIN_TYPE_TO_FIELD_TYPE_MAP[domainType] ?? FieldType.TEXT;
      isMulti = variableTypeConfigSpec.kind === 'array';
      break;
    }
    default:
      fieldType = FieldType.TEXT;
      break;
  }

  return {
    fieldType,
    isMulti,
  };
}

/**
 * Checks if a variable type config spec contains FILE domain types.
 * FILE domain types should not be mapped to schema fields.
 */
function isFileVariable(variableTypeConfigSpec: PipelineConfigJsonSpec): boolean {
  const scalarSpec =
    variableTypeConfigSpec.kind === 'array' ? variableTypeConfigSpec.elementSpec : variableTypeConfigSpec;
  return scalarSpec?.kind === 'domain' && scalarSpec.types?.includes('FILE');
}

/**
 * Checks if a variable can be mapped to a schema field.
 */
function isSchemaFieldMappableVariable(variableTypeConfigSpec: PipelineConfigJsonSpec): boolean {
  return (
    variableTypeConfigSpec?.kind &&
    SCHEMA_FIELD_MAPPED_TEMPLATE_VARIABLE_KINDS.has(variableTypeConfigSpec.kind) &&
    !isFileVariable(variableTypeConfigSpec)
  );
}

/**
 * Generates run field specifications from analysis template config variables.
 *
 * @param analysisTemplate - The analysis template containing config variables
 * @returns Array of schema field specifications defined by the analysis template variables
 */
export function generateRunFieldsSpecFromAnalysisTemplateVariables(
  analysisTemplate: AnalysisTemplateGraphQL
): Array<AnalysisTemplateVariableSchemaFieldSpec> {
  return analysisTemplate.configVariables
    .filter((variable: AnalysisTemplateConfigVariableGraphQL) => {
      const variableTypeConfigSpec = variable.variableTypeConfigSpec as PipelineConfigJsonSpec;
      return isSchemaFieldMappableVariable(variableTypeConfigSpec);
    })
    .map((variable: AnalysisTemplateConfigVariableGraphQL) => {
      const variableTypeConfigSpec = variable.variableTypeConfigSpec as PipelineConfigJsonSpec;
      const {fieldType, isMulti} = getFieldTypeAndMultiFromVariableSpec(variableTypeConfigSpec);
      return {
        name: variable.name,
        systemName: convertApiIdToSchemaFieldSystemName(variable.id),
        fieldType,
        isMulti,
        isRequired: variableTypeConfigSpec.required,
        variableId: variable.id,
      };
    });
}

type AnalysisTemplate = AnalysisTemplateNode | AnalysisTemplateGraphQL;

/**
 * Determines if a field is associated with an analysis template based on its system/warehouse name.
 * Fields are associated if the field's system name matches any of the analysis template variable IDs
 * processed by convertApiIdToSchemaFieldSystemName.
 *
 * @param field - The schema field to check
 * @param analysisTemplate - The analysis template containing config variables
 * @returns True if the field is associated with the analysis template
 */
export function isFieldAssociatedWithAnalysisTemplate<T extends {systemName: string}>(
  field: T,
  analysisTemplate: AnalysisTemplate
): boolean {
  const fieldSystemName = field.systemName;

  const templateVariableSystemNames = analysisTemplate.configVariables
    .filter((variable) => {
      const variableTypeConfigSpec = variable.variableTypeConfigSpec as PipelineConfigJsonSpec;
      return (
        variableTypeConfigSpec?.kind &&
        SCHEMA_FIELD_MAPPED_TEMPLATE_VARIABLE_KINDS.has(variableTypeConfigSpec.kind) &&
        !isFileVariable(variableTypeConfigSpec)
      );
    })
    .map((variable) => convertApiIdToSchemaFieldSystemName(variable.id));

  return templateVariableSystemNames.includes(fieldSystemName);
}

/**
 * Filters schema fields into those associated with analysis template and those that are not.
 *
 * @param fields - Array of schema fields to filter
 * @param analysisTemplate - The analysis template to check against, or null if no template selected
 * @returns Object containing template-associated and regular fields arrays
 */
export function filterFieldsByAnalysisTemplateAssociation<T extends {systemName: string}>(
  fields: Array<T>,
  analysisTemplate: AnalysisTemplate | null
): {
  runFields: Array<T>;
  templateFields: Array<T>;
} {
  if (!analysisTemplate) {
    return {runFields: fields, templateFields: []};
  }

  const templateFields: Array<T> = [];
  const runFields: Array<T> = [];

  for (const field of fields) {
    if (isFieldAssociatedWithAnalysisTemplate(field, analysisTemplate)) {
      templateFields.push(field);
    } else {
      runFields.push(field);
    }
  }

  return {runFields, templateFields};
}

/**
 * Filters fields by persisted analysis template variable mapping (schema_field_api_id -> variable_id).
 * Used when displaying a persisted analysis template.
 *
 * @param allFields - All schema fields
 * @param analysisTemplateVariableMapping - Mapping of schema field API IDs to analysis variable IDs
 * @returns Object containing run fields and template fields
 */
export function filterFieldsByPersistedMapping<T extends {apiId?: string}>(
  allFields: Array<T>,
  analysisTemplateVariableMapping: Record<string, string> | null
): {
  runFields: Array<T>;
  templateFields: Array<T>;
} {
  if (!analysisTemplateVariableMapping || Object.keys(analysisTemplateVariableMapping).length === 0) {
    return {runFields: allFields, templateFields: []};
  }

  const templateFields: Array<T> = [];
  const runFields: Array<T> = [];

  for (const field of allFields) {
    if (field.apiId && analysisTemplateVariableMapping[field.apiId] != null) {
      templateFields.push(field);
    } else {
      runFields.push(field);
    }
  }

  return {runFields, templateFields};
}

/**
 * Filters out draft template fields from all fields by system name.
 * Used when previewing a new analysis template.
 *
 * @param allFields - All schema fields
 * @param draftTemplateFields - Draft template fields to filter out
 * @returns Object containing run fields and template fields
 */
export function filterFieldsByDraftTemplateFields<T extends {systemName: string}>(
  allFields: Array<T>,
  draftTemplateFields: Array<T> | null
): {
  runFields: Array<T>;
  templateFields: Array<T>;
} {
  if (!draftTemplateFields || draftTemplateFields.length === 0) {
    return {runFields: allFields, templateFields: []};
  }

  const draftSystemNames = new Set(draftTemplateFields.map((f) => f.systemName));
  const runFields = allFields.filter((f) => !draftSystemNames.has(f.systemName));

  return {runFields, templateFields: draftTemplateFields};
}

/**
 * Main filtering function that handles both persisted and draft template scenarios.
 * Determines which filtering strategy to use based on whether showing persisted or draft template.
 *
 * @param allFields - All schema fields
 * @param effectiveAnalysisTemplateId - Currently selected template ID (can be draft or persisted)
 * @param persistedAnalysisTemplateId - The persisted template ID from backend
 * @param persistedMapping - Persisted analysis template variable mapping (schema_field_api_id -> variable_id)
 * @param draftTemplateFields - Draft template fields (if previewing a new template)
 * @returns Object containing run fields and template fields
 */
export function filterFieldsForAnalysisTemplate<T extends {apiId?: string; systemName: string}>(
  allFields: Array<T>,
  effectiveAnalysisTemplateId: string | null,
  persistedAnalysisTemplateId: string | null,
  persistedMapping: Record<string, string> | null,
  draftTemplateFields: Array<T> | null
): {
  runFields: Array<T>;
  templateFields: Array<T>;
} {
  if (!effectiveAnalysisTemplateId) {
    // When no template is selected, filter out any persisted template fields
    if (persistedAnalysisTemplateId && persistedMapping) {
      const fieldsWithoutPersisted = allFields.filter(
        (field) => !field.apiId || persistedMapping[field.apiId] == null
      );
      return {runFields: fieldsWithoutPersisted, templateFields: []};
    }
    return {runFields: allFields, templateFields: []};
  }

  if (effectiveAnalysisTemplateId === persistedAnalysisTemplateId) {
    return filterFieldsByPersistedMapping(allFields, persistedMapping);
  }

  // Showing draft template - filter out persisted fields first, then apply draft filtering
  const fieldsWithoutPersisted = persistedMapping
    ? allFields.filter((field) => !field.apiId || persistedMapping[field.apiId] == null)
    : allFields;
  return filterFieldsByDraftTemplateFields(fieldsWithoutPersisted, draftTemplateFields);
}
